import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { fetchApiWithError } from '@upright/ui/api/common/fetchers/fetch-with-error';
import { deleteFromApi, getFromApi, postToApi } from '@upright/ui/api/common/fetchers/request-api';
import { queryClient } from '@upright/ui/api/common/react-query';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { useQuery } from '@tanstack/react-query';
import { mapValues, values } from 'lodash';
import { useSelector } from 'react-redux';
import { productLabelRelatives } from '../../../../../common/model/data/products';
import { PatchMixinRequest } from '../../../../../common/model/input/product-mixins/api-types';
import {
  CreateProductRequest,
  CreateProductResponse,
  EditProductRequest,
  EditProductResponse,
  GetAllMarketSizesResponse,
  GetBasicProductDataResponse,
  GetProductDataResponse,
  GetProductHierarchyResponse,
  GetProductStatisticsResponse,
  GetTagAndPhraseCountsResponse,
  SupportedFields,
} from '../../../../../common/model/input/products/api-types';
import {
  AddPhrasesRequest,
  AddPhrasesResponse,
  RemovePhrasesRequest,
  RemovePhrasesResponse,
} from '../../../../../common/model/input/products/phrases/api-types';
import {
  AddRelationsRequest,
  AddRelationsResponse,
  RemoveRelationsRequest,
  RemoveRelationsResponse,
} from '../../../../../common/model/input/products/relations/api-types';
import {
  BasicProductWithGraph,
  ProductAttributeKey,
  ProductsByCodeSlim,
  TaxonomyProblem,
} from '../../../../../common/model/input/products/types';
import { getVersionAndReleaseIfExists } from '@upright/ui/untidy/redux/selectors';
import { ApiResponse } from '../../../common/types';

export const SINGLE_PRODUCT_DATA = 'singleProductData';
export const PRODUCT_HIERARCHY = 'productHierarchy';
export const PRODUCT_STATISTICS = 'productStatistics';
export const ALL_MARKET_SIZES = 'allMarketSizes';
export const TAG_AND_PHRASE_COUNTS = 'tagAndPhraseCounts';

/**
 * Fetch the slim product hierarchy (basic fields + parents/children/leafs)
 * for the given taxonomy version. Replaces the previous bulk
 * `fetchAllProducts` boot load. See PLT-4774.
 */
export async function fetchProductHierarchy(version: string) {
  const response = await fetchApiWithError<GetProductHierarchyResponse>(`/products/${version}/hierarchy`);
  return response;
}

/**
 * View-scoped React Query hook returning the slim products-by-code map for
 * the currently selected taxonomy version. After PLT-4774 the slim
 * hierarchy is fetched lazily by the views that need it rather than at
 * boot — React Query's per-key deduplication makes this safe to call from
 * multiple components in the same view (only one network round trip).
 *
 * Gated on `useIsTeam()`; for non-team users `data` resolves to `null`
 * and consumers should treat that the same as "no products available"
 * (matching the previous `state.data.products === undefined` behaviour).
 */
export function useProductsHierarchy() {
  const isTeam = useIsTeam();
  const { version } = useSelector(getVersionAndReleaseIfExists);
  const enabled = isTeam && Boolean(version);
  return useQuery({
    queryKey: [PRODUCT_HIERARCHY, version],
    queryFn: enabled ? async () => await fetchProductHierarchy(version as string) : () => null,
    enabled,
    // The slim hierarchy is large and rarely changes during a session; treat
    // it as static and rely on explicit invalidations from mutations.
    staleTime: Infinity,
  });
}

/**
 * Convenience accessor: just the products-by-code map (no React Query
 * meta). Use this when migrating an old `useSelector(getProductsByCode)`
 * read — the return type and undefined semantics match.
 */
export function useProductsByCode(): ProductsByCodeSlim | undefined {
  const { data } = useProductsHierarchy();
  return data?.data ?? undefined;
}

/**
 * Cross-component memo keyed by the products-by-code reference. React Query
 * keeps that reference stable across observers, so a single computation per
 * fetched hierarchy is shared with every consumer — restoring the
 * `createSelector` behaviour the pre-PLT-4774 Redux selectors had. With
 * `useMemo` alone, every component instance re-runs the (O(N²)-ish) walk
 * over the whole taxonomy, which froze pages that render many product cards
 * (e.g. the company-internal /internal/products view, where each
 * `<ProductSetProductCard>` calls `useProductTreeCounts()`).
 */
function memoByProductsRef<T>(
  cache: WeakMap<ProductsByCodeSlim, T>,
  products: ProductsByCodeSlim,
  compute: (products: ProductsByCodeSlim) => T,
): T {
  const cached = cache.get(products);
  if (cached !== undefined) return cached;
  const value = compute(products);
  cache.set(products, value);
  return value;
}

const TOP_LEVEL_PRODUCTS_CACHE = new WeakMap<ProductsByCodeSlim, BasicProductWithGraph[]>();
const PRODUCT_TREE_COUNTS_CACHE = new WeakMap<ProductsByCodeSlim, { [code: string]: number }>();
const SORTED_PRODUCTS_LIST_CACHE = new WeakMap<ProductsByCodeSlim, BasicProductWithGraph[]>();

/**
 * Top-level products (no parents, not floating / phenomenon / mixin) sorted
 * by title. Derived from {@link useProductsHierarchy}; replaces the
 * `getTopLevelProducts` Redux selector after PLT-4774.
 */
export function useTopLevelProducts(): BasicProductWithGraph[] | undefined {
  const products = useProductsByCode();
  if (!products) return undefined;
  return memoByProductsRef(TOP_LEVEL_PRODUCTS_CACHE, products, p =>
    values(p)
      .filter(d => d.parents.length === 0)
      .filter(d => !d.floating)
      .filter(d => !d.phenomenon)
      .filter(d => !d.mixin)
      .sort((a, b) => a.title.localeCompare(b.title)),
  );
}

/**
 * Per-product descendant counts (including self). Derived from
 * {@link useProductsHierarchy}; replaces the `getProductTreeCounts` Redux
 * selector after PLT-4774. Walks the entire tree, so do not call in
 * components that don't already need the whole hierarchy.
 */
export function useProductTreeCounts(): { [code: string]: number } | undefined {
  const products = useProductsByCode();
  if (!products) return undefined;
  return memoByProductsRef(PRODUCT_TREE_COUNTS_CACHE, products, p =>
    mapValues(p, product => productLabelRelatives(product.productCode, 'children', p).length + 1),
  );
}

/**
 * Sorted (by productCode) list of slim products. Derived from
 * {@link useProductsHierarchy}; replaces the `getSortedProductsList` Redux
 * selector after PLT-4774.
 */
export function useSortedProductsList(): BasicProductWithGraph[] | undefined {
  const products = useProductsByCode();
  if (!products) return undefined;
  return memoByProductsRef(SORTED_PRODUCTS_LIST_CACHE, products, p =>
    values(p).sort((a, b) => a.productCode.localeCompare(b.productCode)),
  );
}

export function useGetProductStatistics(version: string | undefined) {
  const isTeam = useIsTeam();
  const enabled = isTeam && Boolean(version);
  return useQuery({
    queryKey: [PRODUCT_STATISTICS, version],
    queryFn: enabled
      ? async () => await fetchApiWithError<GetProductStatisticsResponse>(`/products/${version}/statistics`)
      : () => null,
    enabled,
  });
}

export function useGetAllMarketSizes(version: string | undefined) {
  const isTeam = useIsTeam();
  const enabled = isTeam && Boolean(version);
  return useQuery({
    queryKey: [ALL_MARKET_SIZES, version],
    queryFn: enabled
      ? async () => await fetchApiWithError<GetAllMarketSizesResponse>(`/products/${version}/market-sizes`)
      : () => null,
    enabled,
  });
}

export function useGetTagAndPhraseCounts(version: string | undefined) {
  const isTeam = useIsTeam();
  const enabled = isTeam && Boolean(version);
  return useQuery({
    queryKey: [TAG_AND_PHRASE_COUNTS, version],
    queryFn: enabled
      ? async () => await fetchApiWithError<GetTagAndPhraseCountsResponse>(`/products/${version}/tag-and-phrase-counts`)
      : () => null,
    enabled,
  });
}

/**
 * Invalidate the React Query caches that depend on per-product phrase or
 * tag counts. Called after a successful phrase / tag / commerce / market
 * size / relation / ISIC / metadata mutation. See PLT-4774 — heavy
 * attributes no longer live in Redux, so the React Query cache is the
 * source of truth for them and must be refreshed on mutation.
 */
function invalidatePhraseRelatedQueries(productCode: string) {
  void queryClient.invalidateQueries({ queryKey: [SINGLE_PRODUCT_DATA, productCode] });
  void queryClient.invalidateQueries({ queryKey: [TAG_AND_PHRASE_COUNTS] });
  void queryClient.invalidateQueries({ queryKey: [PRODUCT_STATISTICS] });
}

export async function addPhrasesToProduct(productCode: string, phrases: AddPhrasesRequest) {
  const response = await postToApi<AddPhrasesResponse>(`/product/${productCode}/phrases`, phrases);
  if (response) {
    invalidatePhraseRelatedQueries(productCode);
  }
  return response;
}

export async function removePhrasesFromProduct(productCode: string, phrases: RemovePhrasesRequest) {
  const response = await deleteFromApi<RemovePhrasesResponse>(`/product/${productCode}/phrases`, phrases);
  if (response) {
    invalidatePhraseRelatedQueries(productCode);
  }
  return response;
}

function toEditProductMetadataValue(field: string, value: string | boolean | undefined | number) {
  if (field === 'naturality') {
    return value as number;
  } else if (field === 'b2cRatio') {
    return value != null ? (value as number) : value;
  }
  return value != null ? value.toString() : value;
}

/**
 * Invalidate caches that depend on the slim hierarchy / aggregate
 * statistics. Called after a successful create / delete / relation /
 * metadata mutation. See PLT-4774.
 */
function invalidateHierarchyAndAggregateQueries() {
  void queryClient.invalidateQueries({ queryKey: [PRODUCT_HIERARCHY] });
  void queryClient.invalidateQueries({ queryKey: [PRODUCT_STATISTICS] });
  void queryClient.invalidateQueries({ queryKey: [ALL_MARKET_SIZES] });
  void queryClient.invalidateQueries({ queryKey: [TAG_AND_PHRASE_COUNTS] });
}

export async function editProductMetadata(
  productCode: string,
  field: SupportedFields,
  value: string | boolean | undefined | number,
  user: string,
) {
  const body: EditProductRequest = {
    field,
    value: toEditProductMetadataValue(field, value),
    lastModifiedBy: user,
  };
  const response = await fetchOrThrow<EditProductResponse>(`/product/${productCode}`, 'PATCH', body);
  // PLT-4774: basic fields like `title` / `floating` are part of the slim
  // hierarchy payload — refresh it so the in-app hierarchy view stays in
  // sync. The per-product `SINGLE_PRODUCT_DATA` cache is refreshed by the
  // dedicated `useEditProductMetadata` mutation wrapper.
  void queryClient.invalidateQueries({ queryKey: [PRODUCT_HIERARCHY] });
  void queryClient.invalidateQueries({ queryKey: [SINGLE_PRODUCT_DATA, productCode] });
  return response;
}

export async function editMixinMetadata(productCode: string, body: PatchMixinRequest) {
  const response = await fetchOrThrow<EditProductResponse>(`/product-mixin/${productCode}`, 'PATCH', body);
  void queryClient.invalidateQueries({ queryKey: [PRODUCT_HIERARCHY] });
  void queryClient.invalidateQueries({ queryKey: [SINGLE_PRODUCT_DATA, productCode] });
  return response;
}

export async function addRelationToProduct(productCode: string, relations: AddRelationsRequest) {
  const response = await postToApi<AddRelationsResponse>(`/product/${productCode}/relations`, relations);
  if (response) {
    // Relations change parents/children/leafs — refresh the slim hierarchy
    // and the per-product caches for both products involved.
    void queryClient.invalidateQueries({ queryKey: [PRODUCT_HIERARCHY] });
    void queryClient.invalidateQueries({ queryKey: [SINGLE_PRODUCT_DATA, productCode] });
    void queryClient.invalidateQueries({ queryKey: [SINGLE_PRODUCT_DATA, relations.productCode] });
  }
  return response;
}

export async function removeRelationFromProduct(productCode: string, relations: RemoveRelationsRequest) {
  const response = await deleteFromApi<RemoveRelationsResponse>(`/product/${productCode}/relations`, relations);
  if (response) {
    void queryClient.invalidateQueries({ queryKey: [PRODUCT_HIERARCHY] });
    void queryClient.invalidateQueries({ queryKey: [SINGLE_PRODUCT_DATA, productCode] });
    void queryClient.invalidateQueries({ queryKey: [SINGLE_PRODUCT_DATA, relations.productCode] });
  }
  return response;
}

export async function createProduct(productData: CreateProductRequest) {
  const response = await postToApi<CreateProductResponse>(`/product`, productData);
  if (response) {
    // A new product changes the slim hierarchy, total counts, and (if
    // tagged) per-product aggregates — refresh them all.
    invalidateHierarchyAndAggregateQueries();
  }
  return response;
}

export async function fetchTaxonomyProblems(): Promise<ApiResponse<TaxonomyProblem[]>> {
  return await fetchApiWithError<TaxonomyProblem[]>(`/product-problems`);
}

export async function fetchPhraseProducts(phrase: string) {
  return await getFromApi<string[]>(`/scores/phrase-products/${encodeURIComponent(phrase)}`);
}

export async function fetchBasicProductData(productCodes: string[], version: string) {
  return await postToApi<GetBasicProductDataResponse>(`/products/basic-data/${version}`, {
    codes: productCodes,
  });
}

export async function getProductData<T extends ProductAttributeKey>(
  productCode: string,
  version: string,
  attributes?: T[],
) {
  const parameters = toQueryParameters(attributes);
  const url = `/products/${version}/${productCode}`;
  const urlWithParameters = parameters ? `${url}?${parameters}` : url;
  return await fetchOrThrow<GetProductDataResponse<T>>(urlWithParameters, 'GET');
}

export function useGetProductData<T extends ProductAttributeKey>(
  productCode: string,
  version: string,
  attributes?: T[],
) {
  const isTeam = useIsTeam();

  return useQuery({
    queryKey: [SINGLE_PRODUCT_DATA, productCode, version, attributes],
    queryFn: isTeam ? async () => await getProductData(productCode, version, attributes) : () => null,
    retry: 3,
    refetchOnMount: true,
    enabled: isTeam,
  });
}

function toQueryParameters(keys?: string[]) {
  if (!keys) {
    return undefined;
  }
  return keys.map(key => `${key}=true`).join('&');
}
